// v2/components/Select.js — ortak acilir secici (native <select> yerine)
// Native select'in acilan listesi isletim sistemine aittir ve bicimlendirilemez.
// Bu bilesen listeyi de uygulamanin kendi gorunumunde cizer.
//   - klavye: ok tuslari, Enter, Esc, Home/End
//   - asagi sigmazsa yukari acilir, dis tiklama ve kaydirmada kapanir
//   - 12'den fazla secenekte liste ici arama
import React, {
  useState, useRef, useEffect, useCallback, useMemo, useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search } from 'lucide-react';
import styles from './Select.module.css';

const ARAMA_ESIGI = 12;
const PANEL_MAX = 320;
const PANEL_MIN_W = 190;

export default function Select({
  value = '', onChange = () => {}, options = [],
  placeholder = 'Seçiniz', disabled = false, on = false,
  block = false, className = '', ariaLabel, arama = 'auto',
}) {
  const [acik, setAcik] = useState(false);
  const [q, setQ] = useState('');
  const [aktif, setAktif] = useState(-1);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);

  const secili = options.find((o) => String(o.value) === String(value)) || null;
  const aramaVar = arama === true || (arama === 'auto' && options.length > ARAMA_ESIGI);

  const liste = useMemo(() => {
    const t = q.trim().toLocaleLowerCase('tr');
    if (!t) return options;
    return options.filter((o) => String(o.label).toLocaleLowerCase('tr').includes(t));
  }, [options, q]);

  const olc = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const altBos = window.innerHeight - r.bottom - 12;
    const ustBos = r.top - 12;
    const yukari = altBos < 200 && ustBos > altBos;
    const alan = Math.max(yukari ? ustBos : altBos, 150);
    const w = Math.max(r.width, PANEL_MIN_W);
    setPos({
      left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)),
      width: w,
      top: yukari ? null : Math.round(r.bottom + 6),
      bottom: yukari ? Math.round(window.innerHeight - r.top + 6) : null,
      maxH: Math.min(PANEL_MAX, alan),
    });
  }, []);

  useLayoutEffect(() => { if (acik) olc(); }, [acik, olc]);

  useEffect(() => {
    if (!acik) return undefined;
    const disari = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setAcik(false);
    };
    const kaydir = (e) => {
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setAcik(false);
    };
    document.addEventListener('pointerdown', disari, true);
    window.addEventListener('scroll', kaydir, true);
    window.addEventListener('resize', olc);
    return () => {
      document.removeEventListener('pointerdown', disari, true);
      window.removeEventListener('scroll', kaydir, true);
      window.removeEventListener('resize', olc);
    };
  }, [acik, olc]);

  useEffect(() => {
    if (!acik) { setQ(''); setAktif(-1); return undefined; }
    setAktif(options.findIndex((o) => String(o.value) === String(value)));
    const t = setTimeout(() => {
      if (aramaVar && inputRef.current) inputRef.current.focus();
      else if (panelRef.current) panelRef.current.focus();
    }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acik]);

  useEffect(() => {
    if (!acik || aktif < 0 || !panelRef.current) return;
    const el = panelRef.current.querySelector(`[data-i="${aktif}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [aktif, acik]);

  const sec = useCallback((v) => {
    onChange(v);
    setAcik(false);
    if (btnRef.current) btnRef.current.focus();
  }, [onChange]);

  const tus = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault(); setAcik(false);
      if (btnRef.current) btnRef.current.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault(); setAktif((i) => Math.min(i + 1, liste.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); setAktif((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault(); setAktif(0);
    } else if (e.key === 'End') {
      e.preventDefault(); setAktif(liste.length - 1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (liste[aktif]) sec(liste[aktif].value);
    } else if (e.key === 'Tab') {
      setAcik(false);
    }
  };

  const btnTus = (e) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); setAcik(true);
    }
  };

  return (
    <span className={`${styles.wrap} ${block ? styles.block : ''} ${className}`}>
      <button
        ref={btnRef} type="button" disabled={disabled}
        className={`${styles.trigger} ${on ? styles.triggerOn : ''} ${acik ? styles.triggerOpen : ''}`}
        onClick={() => !disabled && setAcik((a) => !a)}
        onKeyDown={btnTus}
        aria-haspopup="listbox" aria-expanded={acik} aria-label={ariaLabel}
      >
        <span className={secili ? styles.val : styles.ph}>
          {secili ? secili.label : placeholder}
        </span>
        <ChevronDown size={14} className={`${styles.arrow} ${acik ? styles.arrowOpen : ''}`} />
      </button>

      {acik && pos && createPortal(
        <div
          ref={panelRef} tabIndex={-1} role="listbox" aria-label={ariaLabel}
          className={styles.panel}
          style={{
            left: pos.left, width: pos.width, maxHeight: pos.maxH,
            ...(pos.top !== null ? { top: pos.top } : { bottom: pos.bottom }),
          }}
          onKeyDown={tus}
        >
          {aramaVar && (
            <div className={styles.searchRow}>
              <Search size={13} className={styles.searchIcon} />
              <input
                ref={inputRef} className={styles.search} type="text"
                value={q} onChange={(e) => { setQ(e.target.value); setAktif(0); }}
                placeholder="Ara…" aria-label="Listede ara"
              />
            </div>
          )}

          <div className={styles.list}>
            {liste.map((o, i) => {
              const secilliMi = String(o.value) === String(value);
              return (
                <button
                  key={o.value} type="button" data-i={i} role="option"
                  aria-selected={secilliMi}
                  className={`${styles.opt} ${secilliMi ? styles.optOn : ''} ${i === aktif ? styles.optActive : ''}`}
                  onMouseEnter={() => setAktif(i)}
                  onClick={() => sec(o.value)}
                >
                  <span className={styles.optLabel}>{o.label}</span>
                  {o.hint !== undefined && <span className={styles.optHint}>{o.hint}</span>}
                  {secilliMi && <Check size={14} className={styles.optCheck} />}
                </button>
              );
            })}
            {!liste.length && <span className={styles.bos}>Sonuç yok</span>}
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}
